import { useState } from 'react'
import { parseCoordinate, formatDDPair, formatDMSPair, ddToDms, detectCoordinateFormat } from '../../utils/geo/formats'
import { ddToMgrs, formatMgrs, mgrsToDD } from '../../utils/geo/mgrs'
import { useToast } from '../../hooks/useToast'

export default function FormatConverter() {
  const [input, setInput] = useState('')
  const { toast } = useToast()

  const parsed = input.trim() ? parseCoordinate(input) : null

  // Resolve to DD first
  let lat: number | null = null
  let lon: number | null = null

  if (parsed?.format === 'dd') {
    lat = parsed.lat
    lon = parsed.lon
  } else if (parsed?.format === 'mgrs') {
    const dd = mgrsToDD(parsed.mgrs)
    if (dd) { lat = dd.lat; lon = dd.lon }
  } else if (parsed?.format === 'dms') {
    // Already handled by parseCoordinate returning dd
  }

  const mgrsRaw = lat !== null && lon !== null ? ddToMgrs(lat, lon) : null
  const mgrs = mgrsRaw ? formatMgrs(mgrsRaw) : null

  const outputs = lat !== null && lon !== null
    ? [
        { label: 'DD', value: formatDDPair(lat, lon) },
        {
          label: 'DMS',
          value: formatDMSPair({
            format: 'dms',
            lat: ddToDms(lat, 'lat'),
            lon: ddToDms(lon, 'lon'),
          }),
        },
        { label: 'MGRS', value: mgrs ?? 'N/A' },
        { label: 'GeoJSON', value: `[${lon}, ${lat}]` },
      ]
    : []

  function copy(text: string, label: string) {
    navigator.clipboard.writeText(text)
    toast({ title: `Copied ${label}`, description: text })
  }

  function copyAll() {
    const text = outputs.map((o) => `${o.label}: ${o.value}`).join('\n')
    navigator.clipboard.writeText(text)
    toast({ title: 'Copied all formats' })
  }

  const fmt = input ? detectCoordinateFormat(input) : null

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <label className="text-sm font-medium">Input Coordinate</label>
        <div className="relative">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder="Any format: DD, DMS, MGRS, UTM"
            className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm font-mono shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          />
          {fmt && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs bg-blue-500/20 text-blue-400 rounded px-1.5 py-0.5">
              {fmt.toUpperCase()}
            </span>
          )}
        </div>
      </div>

      {outputs.length > 0 && (
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className="text-sm font-medium">Converted Formats</span>
            <button
              onClick={copyAll}
              className="text-xs text-blue-400 hover:text-blue-300 underline underline-offset-2"
            >
              Copy All
            </button>
          </div>
          <div className="rounded-md border border-border divide-y divide-border font-mono text-xs">
            {outputs.map(({ label, value }) => (
              <button
                key={label}
                onClick={() => copy(value, label)}
                className="flex w-full items-center gap-3 px-3 py-2 hover:bg-accent/50 transition-colors group"
              >
                <span className="text-muted-foreground w-16 text-left">{label}</span>
                <span className="flex-1 text-left truncate">{value}</span>
                <span className="opacity-0 group-hover:opacity-100 text-blue-400 shrink-0">Copy</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {input && !outputs.length && (
        <p className="text-xs text-muted-foreground">
          Unrecognized format. Try: <span className="font-mono">38.889, -77.035</span> or{' '}
          <span className="font-mono">18S UJ 23395 09512</span>
        </p>
      )}
    </div>
  )
}
